import { displayText } from '../terminology/store';
import { useTerminology } from '../terminology/TerminologyContext';
import React, { useState, useRef, useEffect, useContext, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ThemeContext from './ThemeContext';
import logo from '../Assets/LOGO-GENYDMS(2).png';  // ← updated logo
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';





import {
  getCsrfToken,
  getCases,
  getDepositionsByCase,
  getArbitrationsByCase,
  getTrialsByCase,
  getMediationsByCase,
  getWitnessPrepsByCase,
} from './services/api';

// Separate Popup component with memoization
const Popup = memo(
  ({
    show,
    onClose,
    title,
    children,
    onConfirm,
    confirmText = 'Join',
    cancelText = 'Cancel',
    theme,
    isLoading,
  }) => {
  useTerminology();
    if (!show) return null;
    return (
      <div
        className="popup-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050,
        }}
      >
        <div
          className="popup-box"
          style={{
            backgroundColor: theme.colors.bgPrimary,
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            textAlign: 'center',
            maxWidth: '460px',
            width: '90%',
            minHeight: '280px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h5
              style={{
                color: theme.colors.textWhite,
                backgroundColor: theme.colors.primary,
                padding: '10px 40px',
                borderRadius: '5px',
                display: 'inline-block',
                width: '400px',
                maxWidth: '100%',
                margin: '0 auto 15px',
                boxSizing: 'border-box',
              }}
            >
              {title}
            </h5>
            {children}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '10px',
              marginTop: '20px',
            }}
          >
            <button
              className="btn btn-secondary"
              style={{
                backgroundColor: theme.colors.secondary,
                color: theme.colors.textWhite,
                border: 'none',
                padding: '8px 16px',
                borderRadius: '5px',
                cursor: 'pointer',
                width: '48%',
                fontSize: 'clamp(0.78rem, 1.9vw, 0.88rem)',
              }}
              onClick={onClose}
            >
              {cancelText}
            </button>
            <button
              className="btn btn-primary"
              style={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.textWhite,
                border: 'none',
                padding: '8px 16px',
                borderRadius: '5px',
                cursor: 'pointer',
                width: '48%',
                fontSize: 'clamp(0.78rem, 1.9vw, 0.88rem)',
              }}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Validating...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

const Header = ({ onJoinSession, onDropdownLogout }) => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')) || null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [resellerLogo, setResellerLogo] = useState(logo);           // ← updated default
  const [bannerColor, setBannerColor] = useState(theme.colors.bgPrimary);
  const [textColor, setTextColor] = useState(theme.colors.textPrimary);
  const [showJoinPopup, setShowJoinPopup] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [passcode, setPasscode] = useState('');
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}` || 'http://localhost:8000';
  

  // Detect special / contextual pages
  const isSessionPage = ['/session', '/join-session'].some(
  (path) =>
    location.pathname === path ||
    (location.pathname.includes(path) &&
      !location.pathname.match(/\/dashboard\/viewcase\/\d+\/session\/\d+/))
);
  const isJoinSessionPage = location.pathname === '/join-session';
  const isSignInPage =
    location.pathname === '/signin' || location.pathname.includes('/signin/');
  const isWebLoginPage =
    location.pathname === '/member' || location.pathname.includes('/member/');
  const isWebJoinPage =
    location.pathname === '/nonmembers' || location.pathname.includes('/nonmembers/');
  const isSpecialPage = isSignInPage || isWebLoginPage || isWebJoinPage;

  // Validate user on mount (only for non-special pages)
  useEffect(() => {
    if (isSpecialPage) return;
    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    if (!storedUser || !storedUser.username) {
      toast.error('Please log in to continue.', { autoClose: 3000 });
      navigate('/login');
    } else {
      setUser(storedUser);
    }
  }, [navigate, isSpecialPage]);

  useEffect(() => {
    const fetchResellerBranding = async () => {
      if (user?.role === 'reseller' && user?.username) {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            setResellerLogo(logo);
            setBannerColor(theme.colors.bgPrimary);
            setTextColor(theme.colors.textPrimary);
            return;
          }
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/webmanager/resellers/logo/${user.username}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setResellerLogo(response.data.logo || logo);           // ← fallback to new logo
          setBannerColor(response.data.banner_color || theme.colors.bgPrimary);
          setTextColor(response.data.text_color || theme.colors.textPrimary);
        } catch (err) {
          console.error('Error fetching reseller branding:', err);
          setResellerLogo(logo);
          setBannerColor(theme.colors.bgPrimary);
          setTextColor(theme.colors.textPrimary);
        }
      } else {
        setResellerLogo(logo);
        setBannerColor(theme.colors.bgPrimary);
        setTextColor(theme.colors.textPrimary);
      }
    };
    if (user) {
      fetchResellerBranding();
    }
  }, [user, BASE_URL, theme.colors]);

  useEffect(() => {
    const fetchAllSessions = async () => {
      if (!showJoinPopup) return;
      try {
        setIsLoading(true);
        await getCsrfToken();
        const casesResponse = await getCases();
        const cases = casesResponse.data.data || casesResponse.data || [];
        const sessionPromises = cases.map(async (caseItem) => {
          try {
            const [
              depositionsResponse,
              arbitrationsResponse,
              trialsResponse,
              mediationsResponse,
              witnessPrepsResponse,
            ] = await Promise.all([
              getDepositionsByCase(caseItem.id).catch(() => ({ data: [] })),
              getArbitrationsByCase(caseItem.id).catch(() => ({ data: [] })),
              getTrialsByCase(caseItem.id).catch(() => ({ data: [] })),
              getMediationsByCase(caseItem.id).catch(() => ({ data: [] })),
              getWitnessPrepsByCase(caseItem.id).catch(() => ({ data: [] })),
            ]);
            const depositions = (Array.isArray(depositionsResponse.data)
              ? depositionsResponse.data
              : depositionsResponse.data.data || []
            )
              .filter((session) => session.session_type === 'deposition')
              .map((session) => ({
                ...session,
                session_type: 'Deposition',
                normalizedType: 'deposition',
                display_name: session.witness || session.name || 'Unnamed Deposition',
                created_at: session.created_at || new Date().toISOString(),
                status: session.status || 'None',
                session_passcode: session.session_passcode || '',
              }));
            const arbitrations = (Array.isArray(arbitrationsResponse.data)
              ? arbitrationsResponse.data
              : arbitrationsResponse.data.data || []
            )
              .filter((session) => session.session_type === 'arbitration')
              .map((session) => ({
                ...session,
                session_type: 'Arbitration',
                normalizedType: 'arbitration',
                display_name: session.name || 'Unnamed Arbitration',
                created_at: session.created_at || new Date().toISOString(),
                status: session.status || 'None',
                session_passcode: session.session_passcode || '',
              }));
            const trials = (Array.isArray(trialsResponse.data)
              ? trialsResponse.data
              : trialsResponse.data.data || []
            )
              .filter((session) => session.session_type === 'trial')
              .map((session) => ({
                ...session,
                session_type: 'Trial',
                normalizedType: 'trial',
                display_name: session.name || 'Unnamed Trial',
                created_at: session.created_at || new Date().toISOString(),
                status: session.status || 'None',
                session_passcode: session.session_passcode || '',
              }));
            const mediations = (Array.isArray(mediationsResponse.data)
              ? mediationsResponse.data
              : mediationsResponse.data.data || []
            )
              .filter((session) => session.session_type === 'mediation')
              .map((session) => ({
                ...session,
                session_type: 'Mediation',
                normalizedType: 'mediation',
                display_name: session.name || 'Unnamed Mediation',
                created_at: session.created_at || new Date().toISOString(),
                status: session.status || 'None',
                session_passcode: session.session_passcode || '',
              }));
            const witnessPreps = (Array.isArray(witnessPrepsResponse.data)
              ? witnessPrepsResponse.data
              : witnessPrepsResponse.data.data || []
            )
              .filter((session) => session.session_type === 'witness_prep')
              .map((session) => ({
                ...session,
                session_type: 'Witness Prep',
                normalizedType: 'witness-prep',
                display_name:
                  session.witness || session.name || 'Unnamed Witness Prep',
                created_at: session.created_at || new Date().toISOString(),
                status: session.status || 'None',
                session_passcode: session.session_passcode || '',
              }));
            return [
              ...depositions,
              ...arbitrations,
              ...trials,
              ...mediations,
              ...witnessPreps,
            ];
          } catch (error) {
            console.error(`${"Failed to fetch sessions for case "}${caseItem.id}${":"}`, error);
            return [];
          }
        });
        let allSessions = (await Promise.all(sessionPromises)).flat();
        const sessionMap = new Map();
        allSessions.forEach((session) => {
          const key = `${session.id}-${session.session_type}`;
          if (!sessionMap.has(key)) {
            sessionMap.set(key, session);
          }
        });
        allSessions = Array.from(sessionMap.values());
        setSessions(allSessions);
      } catch (error) {
        console.error('Failed to fetch all sessions for validation:', error);
        toast.error(
          displayText('Failed to fetch sessions: ') +
            (error.response?.data?.message || error.message)
        );
        setSessions([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllSessions();
  }, [showJoinPopup]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('webapp_token');
    localStorage.removeItem('user');
    localStorage.removeItem('join_request');
    localStorage.removeItem('currentSession');
    navigate('/signin');
  };

  const handleLogoutClick = () => {
    setShowLogoutPopup(true);
  };

  const confirmLogout = () => {
    handleLogout();
    setShowLogoutPopup(false);
  };

  const closeLogoutPopup = () => {
    setShowLogoutPopup(false);
  };

  const handleWebAppClick = () => {
    window.open('/signin', '_blank');
  };



const handleOneFinTraceClick = () => {
  window.open(
    "https://dev.onefintrace.com/dms/user/login",
    "_blank",
    "noopener,noreferrer"
  );
};

  const handleThemeClick = () => {
    navigate(`/${user?.username}/theme`);
    setIsDropdownOpen(false);
  };

  const handleJoinClick = () => {
    setShowJoinPopup(true);
  };

  const handleJoinSubmit = () => {
    if (!sessionId.trim()) {
      toast.error(displayText('Please enter a Session ID.'));
      return;
    }
    if (!passcode.trim()) {
      toast.error('Please enter a Passcode.');
      return;
    }
    const session = sessions.find(
      (s) => String(s.id) === sessionId && String(s.session_passcode) === passcode
    );
    if (session) {
      localStorage.setItem(
        'currentSession',
        JSON.stringify({
          sessionId,
          type: session.normalizedType,
          userRole: user?.role === 'client_admin' ? 'client_admin' : 'member',
        })
      );
      if (onJoinSession) {
        onJoinSession(sessionId, session.normalizedType);
      }
      navigate('/join-session', { replace: true });
      setShowJoinPopup(false);
      setSessionId('');
      setPasscode('');
      toast.success(displayText('Session joined successfully!'));
    } else {
      toast.error(displayText('Invalid Session ID or Passcode.'));
    }
  };

  const closeJoinPopup = () => {
    setShowJoinPopup(false);
    setSessionId('');
    setPasscode('');
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const userInitial = user?.username?.charAt(0)?.toUpperCase() || 'U';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!user && !isSpecialPage) {
    return null;
  }

  // ── SPECIAL PAGES ──
  if (isSpecialPage) {
    if (isSignInPage) {
      return (
        <header
          style={{
            backgroundColor: bannerColor,
            padding: 'clamp(0.4rem, 1.75vw, 0.8rem) 0.5rem',
            borderBottom: `2px solid ${theme.colors.primary}`,
            position: 'relative',
            zIndex: 1001,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              maxWidth: '100%',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0 clamp(0.25rem, 1.75vw, 0.5rem)',
            }}
          >
            <div className="logo-wrapper">
              <img
                src={resellerLogo}
                alt="Logo"
                className="logo-img"
                onError={(e) => {
                  e.target.src = logo;
                }}
              />
            </div>
          </div>
        </header>
      );
    }

    // /member and /nonmembers → only Back button
    return (
      <header
        style={{
          backgroundColor: bannerColor,
          padding: 'clamp(0.4rem, 1.75vw, 0.8rem) 0.5rem',
          borderBottom: `2px solid ${theme.colors.primary}`,
          position: 'relative',
          zIndex: 1001,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            maxWidth: '100%',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: '0 clamp(0.25rem, 1.75vw, 0.5rem)',
          }}
        >
          <button
            style={{
              background: theme.gradients.primary,
              color: '#ffffff',
              border: 'none',
              padding:
                'clamp(0.18rem, 1.25vw, 0.28rem) clamp(0.5rem, 1.75vw, 0.7rem)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 'clamp(0.62rem, 1.9vw, 0.82rem)',
              fontWeight: 500,
              transition: 'background 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.35rem',
            }}
            onClick={() => navigate('/signin')}
            onMouseOver={(e) =>
              (e.target.style.background =
                theme.gradients.primaryHover || theme.gradients.primary)
            }
            onMouseOut={(e) =>
              (e.target.style.background = theme.gradients.primary)
            }
          >
            <i
              className="bi bi-chevron-left"
              style={{ fontSize: 'clamp(0.7rem, 2vw, 0.85rem)' }}
            ></i>
            Back
          </button>
        </div>
      </header>
    );
  }

  // ── JOIN-SESSION PAGE ───────────
  if (isJoinSessionPage) {
    return (
      <>
        <header
          style={{
            backgroundColor: bannerColor,
            padding: 'clamp(0.4rem, 1.75vw, 0.8rem) 0.5rem',
            borderBottom: `2px solid ${theme.colors.primary}`,
            position: 'relative',
            zIndex: 1001,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              maxWidth: '100%',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: window.innerWidth < 600 ? 'wrap' : 'nowrap',
              flexDirection: window.innerWidth < 600 ? 'column' : 'row',
              gap: 'clamp(0.25rem, 1.75vw, 0.45rem)',
              padding: '0 clamp(0.25rem, 1.75vw, 0.5rem)',
            }}
          >
            <div style={{ display: 'flex', gap: 'clamp(0.25rem, 1.75vw, 0.45rem)' }}>
              <button
                style={{
                  background: theme.gradients.primary,
                  color: '#ffffff',
                  border: 'none',
                  padding:
                    'clamp(0.18rem, 1.25vw, 0.28rem) clamp(0.5rem, 1.75vw, 0.7rem)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 'clamp(0.62rem, 1.9vw, 0.82rem)',
                  fontWeight: 500,
                  transition: 'background 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.35rem',
                  width: window.innerWidth < 600 ? '100%' : 'auto',
                  maxWidth: window.innerWidth < 600 ? '100%' : '135px',
                  boxSizing: 'border-box',
                }}
                onClick={() => navigate('/session')}
                onMouseOver={(e) =>
                  (e.target.style.background =
                    theme.gradients.primaryHover || theme.gradients.primary)
                }
                onMouseOut={(e) =>
                  (e.target.style.background = theme.gradients.primary)
                }
              >
                <i
                  className="bi bi-chevron-left"
                  style={{ fontSize: 'clamp(0.7rem, 2vw, 0.85rem)' }}
                ></i>{displayText(" Cases ")}</button>

              <button
                style={{
                  background: theme.gradients.primary,
                  color: '#ffffff',
                  border: 'none',
                  padding:
                    'clamp(0.18rem, 1.25vw, 0.28rem) clamp(0.5rem, 1.75vw, 0.7rem)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 'clamp(0.62rem, 1.9vw, 0.82rem)',
                  fontWeight: 500,
                  transition: 'background 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: window.innerWidth < 600 ? '100%' : 'auto',
                  maxWidth: window.innerWidth < 600 ? '100%' : '135px',
                  boxSizing: 'border-box',
                }}
                onClick={handleLogoutClick}
                onMouseOver={(e) =>
                  (e.target.style.background =
                    theme.gradients.primaryHover || theme.gradients.primary)
                }
                onMouseOut={(e) =>
                  (e.target.style.background = theme.gradients.primary)
                }
              >
                Logout
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
              <div
                style={{
                  width: 'clamp(22px, 4.5vw, 28px)',
                  height: 'clamp(22px, 4.5vw, 28px)',
                  borderRadius: '50%',
                  background: theme.gradients.secondary,
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(0.95rem, 2.75vw, 1.1rem)',
                  fontWeight: 700,
                }}
              >
                {userInitial}
              </div>
              <span
                style={{
                  color: textColor,
                  fontSize: 'clamp(0.82rem, 2.25vw, 0.95rem)',
                  fontWeight: 500,
                }}
              >
                {user?.username || 'User'}
              </span>
            </div>
          </div>
        </header>

        <Popup
          show={showLogoutPopup}
          onClose={closeLogoutPopup}
          title="Logout"
          onConfirm={confirmLogout}
          confirmText="Yes"
          cancelText="Cancel"
          theme={theme}
          isLoading={false}
        >
          <p style={{ color: theme.colors.textPrimary, margin: '0 0 15px', padding: '20px 0' }}>
            Are you sure you want to logout?
          </p>
        </Popup>
      </>
    );
  }

  // ── SESSION PAGE ───
  if (isSessionPage) {
    return (
      <>
        <header
          style={{
            backgroundColor: bannerColor,
            padding: 'clamp(0.4rem, 1.75vw, 0.8rem) 0.5rem',
            borderBottom: `2px solid ${theme.colors.primary}`,
            position: 'relative',
            zIndex: 1001,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              maxWidth: '100%',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: window.innerWidth < 600 ? 'wrap' : 'nowrap',
              flexDirection: window.innerWidth < 600 ? 'column' : 'row',
              gap: 'clamp(0.25rem, 1.75vw, 0.45rem)',
              padding: '0 clamp(0.25rem, 1.75vw, 0.5rem)',
            }}
          >
            <div style={{ display: 'flex', gap: 'clamp(0.25rem, 1.75vw, 0.45rem)' }}>
              <button
                style={{
                  background: theme.gradients.primary,
                  color: '#ffffff',
                  border: 'none',
                  padding:
                    'clamp(0.18rem, 1.25vw, 0.28rem) clamp(0.5rem, 1.75vw, 0.7rem)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 'clamp(0.62rem, 1.9vw, 0.82rem)',
                  fontWeight: 500,
                  transition: 'background 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: window.innerWidth < 600 ? '100%' : 'auto',
                  maxWidth: window.innerWidth < 600 ? '100%' : '135px',
                  boxSizing: 'border-box',
                }}
                onClick={handleLogoutClick}
                onMouseOver={(e) =>
                  (e.target.style.background =
                    theme.gradients.primaryHover || theme.gradients.primary)
                }
                onMouseOut={(e) =>
                  (e.target.style.background = theme.gradients.primary)
                }
              >
                Logout
              </button>

              <button
                style={{
                  background: theme.gradients.primary,
                  color: '#ffffff',
                  border: 'none',
                  padding:
                    'clamp(0.18rem, 1.25vw, 0.28rem) clamp(0.5rem, 1.75vw, 0.7rem)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 'clamp(0.62rem, 1.9vw, 0.82rem)',
                  fontWeight: 500,
                  transition: 'background 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: window.innerWidth < 600 ? '100%' : 'auto',
                  maxWidth: window.innerWidth < 600 ? '100%' : '135px',
                  boxSizing: 'border-box',
                }}
                onClick={handleJoinClick}
                onMouseOver={(e) =>
                  (e.target.style.background =
                    theme.gradients.primaryHover || theme.gradients.primary)
                }
                onMouseOut={(e) =>
                  (e.target.style.background = theme.gradients.primary)
                }
              >
                Join
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
              <div
                style={{
                  width: 'clamp(22px, 4.5vw, 28px)',
                  height: 'clamp(22px, 4.5vw, 28px)',
                  borderRadius: '50%',
                  background: theme.gradients.secondary,
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(0.95rem, 2.75vw, 1.1rem)',
                  fontWeight: 700,
                }}
              >
                {userInitial}
              </div>
              <span
                style={{
                  color: textColor,
                  fontSize: 'clamp(0.82rem, 2.25vw, 0.95rem)',
                  fontWeight: 500,
                }}
              >
                {user?.username || 'User'}
              </span>
            </div>
          </div>
        </header>

        <Popup
          show={showJoinPopup}
          onClose={closeJoinPopup}
          title={displayText("Join Session")}
          onConfirm={handleJoinSubmit}
          confirmText="Join"
          theme={theme}
          isLoading={isLoading}
        >
          <p style={{ color: theme.colors.textPrimary, margin: '0 0 15px' }}>{displayText(" Please enter a Session ID and passcode to enter a session. ")}</p>
          <div style={{ marginBottom: '15px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <label
                style={{
                  color: theme.colors.textPrimary,
                  fontWeight: 'bold',
                  minWidth: '80px',
                }}
              >{displayText(" Session ID: ")}</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., 2"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                style={{ width: '300px', maxWidth: '100%' }}
              />
            </div>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <label
                style={{
                  color: theme.colors.textPrimary,
                  fontWeight: 'bold',
                  minWidth: '80px',
                }}
              >
                Passcode:
              </label>
              <input
                type="password"
                className="form-control"
                placeholder="Enter passcode"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                style={{ width: '300px', maxWidth: '100%' }}
              />
            </div>
          </div>
        </Popup>

        <Popup
          show={showLogoutPopup}
          onClose={closeLogoutPopup}
          title="Logout"
          onConfirm={confirmLogout}
          confirmText="Yes"
          cancelText="Cancel"
          theme={theme}
          isLoading={false}
        >
          <p style={{ color: theme.colors.textPrimary, margin: '0 0 15px', padding: '20px 0' }}>
            Are you sure you want to logout?
          </p>
        </Popup>
      </>
    );
  }

  // ── DEFAULT DASHBOARD HEADER ────
  return (
    <header
      style={{
        backgroundColor: bannerColor,
        padding: 'clamp(0.4rem, 1.75vw, 0.8rem) 0.5rem',
        borderBottom: `2px solid ${theme.colors.primary}`,
        position: 'relative',
        zIndex: 1001,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth: '100%',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: window.innerWidth < 600 ? 'wrap' : 'nowrap',
          flexDirection: window.innerWidth < 600 ? 'column' : 'row',
          gap: 'clamp(0.25rem, 1.75vw, 0.45rem)',
          padding: '0 clamp(0.25rem, 1.75vw, 0.5rem)',
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginLeft: '300px',
            width: window.innerWidth < 600 ? '100%' : 'auto',
            order: window.innerWidth < 600 ? 1 : 0,
          }}
        >
          <div className="logo-wrapper">
            <img
              src={resellerLogo}
              alt="Logo"
              className="logo-img"
              onError={(e) => {
                e.target.src = logo;
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'clamp(0.25rem, 1.75vw, 0.45rem)',
            width: window.innerWidth < 600 ? '100%' : 'auto',
            justifyContent: 'flex-end',
            order: window.innerWidth < 600 ? 2 : 1,
          }}
        >
          <button
            style={{
              background: theme.gradients.primary,
              // background : '#32CD32',
              color: '#ffffff',
              border: 'none',
              padding:
                'clamp(0.18rem, 1.25vw, 0.28rem) clamp(0.5rem, 1.75vw, 0.7rem)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 'clamp(0.62rem, 1.9vw, 0.82rem)',
              fontWeight: 500,
              transition: 'background 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: window.innerWidth < 600 ? '100%' : 'auto',
              maxWidth: window.innerWidth < 600 ? '100%' : '135px',
              boxSizing: 'border-box',
            }}
            onClick={handleWebAppClick}
            onMouseOver={(e) =>
              (e.target.style.background = theme.gradients.primaryHover)
            }
            onMouseOut={(e) =>
              (e.target.style.background = theme.gradients.primary)
            }
          >
            Go to Web App
          </button>

           <button
            style={{
              background: theme.gradients.primary,
              // background : '#32CD32',
              color: '#ffffff',
              border: 'none',
              padding:
                'clamp(0.18rem, 1.25vw, 0.28rem) clamp(0.5rem, 1.75vw, 0.7rem)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 'clamp(0.62rem, 1.9vw, 0.82rem)',
              fontWeight: 500,
              transition: 'background 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: window.innerWidth < 600 ? '100%' : 'auto',
              maxWidth: window.innerWidth < 600 ? '100%' : '135px',
              boxSizing: 'border-box',
            }}
            onClick={handleOneFinTraceClick}
            onMouseOver={(e) =>
              (e.target.style.background = theme.gradients.primaryHover)
            }
            onMouseOut={(e) =>
              (e.target.style.background = theme.gradients.primary)
            }
          >
             OneFinTrace
          </button>


          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              width: window.innerWidth < 600 ? '100%' : 'auto',
            }}
            ref={dropdownRef}
          >
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '0.22rem',
                width: '100%',
                justifyContent: 'flex-end',
              }}
              onClick={toggleDropdown}
            >
              <div
                style={{
                  width: 'clamp(22px, 4.5vw, 28px)',
                  height: 'clamp(22px, 4.5vw, 28px)',
                  borderRadius: '50%',
                  background: theme.gradients.secondary,
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(0.95rem, 2.75vw, 1.1rem)',
                  fontWeight: 700,
                  marginRight: '0.45rem',
                }}
              >
                {userInitial}
              </div>
              <span
                style={{
                  color: textColor,
                  fontSize: 'clamp(0.82rem, 2.25vw, 0.95rem)',
                  fontWeight: 500,
                  marginRight: '0.6rem',
                  flexGrow: window.innerWidth < 600 ? 1 : 0,
                  textAlign: 'right',
                }}
              >
                {user?.username || 'User'}
              </span>
              <i
                className="bi bi-chevron-down"
                style={{
                  fontSize: 'clamp(0.82rem, 2.25vw, 0.95rem)',
                  color: textColor,
                  transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              ></i>
            </button>

           {isDropdownOpen && (
  <div
    style={{
      position: 'absolute',
      top: '100%',
      right: 0,
      backgroundColor: theme.colors.bgPrimary,
      border: `1px solid ${theme.colors.primary}`,
      borderRadius: '4px',
      boxShadow: `0 2px 5px ${theme.colors.shadow}`,
      zIndex: 1,
      minWidth: '140px',
      marginTop: '0.25rem',
    }}
  >
    {user?.role === 'superadmin' && (
      <button
        type="button"
        style={{
          background: 'none',
          border: 'none',
          padding: '0.45rem 0.9rem',
          cursor: 'pointer',
          color: textColor,
          fontSize: 'clamp(0.78rem, 1.9vw, 0.88rem)',
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: '0.45rem',
          whiteSpace: 'nowrap',
        }}
        onClick={() => {
          navigate('/superadmin/terminology');
          setIsDropdownOpen(false);
        }}
        onMouseOver={(e) =>
          (e.currentTarget.style.backgroundColor = theme.colors.bgSecondary)
        }
        onMouseOut={(e) =>
          (e.currentTarget.style.backgroundColor = 'transparent')
        }
      >
        <i className="bi bi-fonts" />
        Terminology Settings
      </button>
    )}
    {/* Theme Button */}
    <button
      style={{
        background: 'none',
        border: 'none',
        padding: '0.45rem 0.9rem',
        cursor: 'pointer',
        color: textColor,
        fontSize: 'clamp(0.78rem, 1.9vw, 0.88rem)',
        width: '100%',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: '0.45rem',
      }}
      onClick={() => {
        handleThemeClick();
        setIsDropdownOpen(false);
      }}
      onMouseOver={(e) =>
        (e.target.style.backgroundColor = theme.colors.bgSecondary)
      }
      onMouseOut={(e) =>
        (e.target.style.backgroundColor = 'transparent')
      }
    >
      <i className="bi bi-palette" />
      Theme
    </button>

    {/* LOGOUT ITEM */}
    <button
      style={{
        background: 'none',
        border: 'none',
        padding: '0.45rem 0.9rem',
        cursor: 'pointer',
        color: textColor,
        fontSize: 'clamp(0.78rem, 1.9vw, 0.88rem)',
        width: '100%',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: '0.45rem',
      }}
      onClick={() => {
  setIsDropdownOpen(false);

  if (onDropdownLogout) {
    onDropdownLogout();
  }
}}
      onMouseOver={(e) =>
        (e.target.style.backgroundColor = theme.colors.bgSecondary)
      }
      onMouseOut={(e) =>
        (e.target.style.backgroundColor = 'transparent')
      }
    >
      <i className="bi bi-box-arrow-right" />
      Logout
    </button>

  </div>
)}


          </div>
        </div>
      </div>
    </header>
  );
};

const Footer = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user'));
  const [bannerColor, setBannerColor] = useState(theme.colors.bgPrimary);
  const [textColor, setTextColor] = useState(theme.colors.textSecondary);
  const [resellerLogo, setResellerLogo] = useState(logo);   // ← updated default
  const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}` || 'http://localhost:8000';
const isWebManagerSession =
  location.pathname.includes('/dashboard/viewcase/') &&
  location.pathname.includes('/session/');
  const isSignInPage = location.pathname === '/signin' || location.pathname.includes('/signin/');
  const isWebLoginPage = location.pathname === '/member' || location.pathname.includes('/member/');
  const isWebJoinPage = location.pathname === '/nonmembers' || location.pathname.includes('/nonmembers/');
  const isSpecialPage =
  !isWebManagerSession &&
  ['/signin', '/member', '/nonmembers', '/session', '/join-session'].some(
    (path) => location.pathname === path || location.pathname.includes(`${path}/`)
  );
  const hideFooterLogo = isWebLoginPage || isWebJoinPage;

  useEffect(() => {
    const fetchResellerBranding = async () => {
      if (user?.role === 'reseller' && user?.username) {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            setBannerColor(theme.colors.bgPrimary);
            setTextColor(theme.colors.textSecondary);
            setResellerLogo(logo);
            return;
          }
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/webmanager/resellers/logo/${user.username}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBannerColor(response.data.banner_color || theme.colors.bgPrimary);
          setTextColor(response.data.text_color || theme.colors.textSecondary);
          setResellerLogo(response.data.logo || logo);
        } catch (err) {
          setBannerColor(theme.colors.bgPrimary);
          setTextColor(theme.colors.textSecondary);
          setResellerLogo(logo);
        }
      } else {
        setBannerColor(theme.colors.bgPrimary);
        setTextColor(theme.colors.textSecondary);
        setResellerLogo(logo);
      }
    };
    fetchResellerBranding();
  }, [user, BASE_URL, theme.colors]);

  return (
    <footer
      style={{
        backgroundColor: bannerColor,
        padding: 'clamp(0.75rem, 1.75vw, 1rem) 0.5rem',
        borderTop: `2px solid ${theme.colors.primary}`,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth: '100%',
          margin: '0 auto',
          display: 'flex',
          justifyContent: isSpecialPage ? 'space-between' : 'flex-start',
          alignItems: 'center',
          width: '100%',
          padding: '0 clamp(0.25rem, 1.75vw, 0.5rem)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'clamp(0.68rem, 1.9vw, 0.82rem)',
            color: textColor,
            textAlign: 'left',
          }}
        >
          © 2026 OneFinCore. All Rights Reserved.
        </p>

        {isSpecialPage && !hideFooterLogo && (
          <div className="logo-wrapper" style={{ maxHeight: '30px', maxWidth: '100px' }}>
            <img
              src={resellerLogo}
              alt="Secondary Logo"
              className="logo-img"
              onError={(e) => {
                e.target.src = logo;
              }}
            />
          </div>
        )}
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Global typography
  useEffect(() => {
    const styleId = 'dynamic-theme-typography';
    let styleTag = document.getElementById(styleId);
    const fontFamily = theme.typography?.fontFamily || "'Roboto', sans-serif";
    const titleSize = theme.typography?.titleFontSize || 24;
    const bodySize = theme.typography?.bodyFontSize || 14;

    const css = `
      body {
        font-family: ${fontFamily};
        font-size: ${bodySize}px;
      }
      h1, h2, h3, h4, h5, h6 {
        font-family: ${fontFamily};
        font-size: ${titleSize}px;
      }
    `;

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = css;
  }, [theme.typography]);

  const handleJoinSession = (sessionId, sessionType) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.setItem(
      'currentSession',
      JSON.stringify({
        sessionId: String(sessionId),
        type: sessionType,
        userRole: user.role === 'client_admin' ? 'client_admin' : 'member',
      })
    );
    navigate('/join-session', { replace: true });
  };

  const handleDropdownLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('webapp_token');
    localStorage.removeItem('user');
    localStorage.removeItem('join_request');
    localStorage.removeItem('currentSession');
    navigate('/login');
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        fontFamily: theme.typography?.fontFamily || "'Roboto', sans-serif",
        fontSize: `${theme.typography?.bodyFontSize || 14}px`,
        position: 'relative',
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: theme.colors.bgPrimary,
      }}
    >
      <Header
        onJoinSession={handleJoinSession}
        onDropdownLogout={handleDropdownLogout}
      />
      <main
        style={{
          flex: 1,
          maxWidth: '100%',
          margin: '0 auto',
          width: '100%',
          padding: window.innerWidth < 600 ? '1rem 0.25rem' : '1.5rem 0.5rem',
          boxSizing: 'border-box',
        }}
      >
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;